/**
 * P&L month-detail sections (A7Y-2054): the per-month breakdown rendered below
 * the month-detail page header. Presentational — takes a `PnlMonthDetail`.
 *
 * Sections:
 *   1. Month totals (spend / revenue / net)
 *   2. Spend by category — table with color dots matching the Overview charts
 *   3. Spend by vendor — table, already descending from the API
 *   4. Revenue by org — cards with tier badge, billing email, members, amount
 *
 * Empty arrays render intentional empty states, not blank tables — the norm
 * here is a month with a single paying org (or none at all).
 */
import { cn, formatCurrency } from "@/lib/utils";
import type { PnlMonthDetail, PnlOrgRevenue } from "@a7y/types";
import { Badge } from "@a7y/tailwind-catalyst";
import { Building2, Mail, Users } from "lucide-react";
import { categoryColor } from "../palette";

type BadgeColor = "sky" | "indigo" | "violet" | "emerald" | "zinc";

const TIER_BADGE_COLOR: Record<string, BadgeColor> = {
  free: "zinc",
  basic: "sky",
  pro: "indigo",
  elite: "violet",
  enterprise: "emerald",
  other: "zinc",
};

function tierLabel(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function netClass(net: number): string {
  return net > 0
    ? "text-emerald-600 dark:text-emerald-400"
    : net < 0
      ? "text-rose-600 dark:text-rose-400"
      : "text-zinc-900 dark:text-white";
}

function signedCurrency(net: number): string {
  return `${net < 0 ? "−" : ""}${formatCurrency(Math.abs(net))}`;
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">{title}</h2>
      {children}
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">{children}</p>
  );
}

function MiniMetric({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{label}</p>
      <p
        className={cn(
          "mt-1.5 text-2xl font-semibold tracking-tight tabular-nums",
          valueClass ?? "text-zinc-950 dark:text-white",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function OrgRow({ org }: { org: PnlOrgRevenue }) {
  const badgeColor = TIER_BADGE_COLOR[org.tier] ?? "zinc";
  return (
    <li className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 className="h-4 w-4 shrink-0 text-zinc-400" />
            <span className="font-medium text-zinc-900 dark:text-white truncate">
              {org.orgName}
            </span>
            <Badge color={badgeColor}>{tierLabel(org.tier)}</Badge>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
            <Mail className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{org.billingEmail || "—"}</span>
          </div>
          {org.members.length > 0 ? (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
              <Users className="h-3.5 w-3.5 shrink-0 mt-px" />
              <span>
                {org.members
                  .map((m) => m.name || m.email)
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span>No members on file</span>
            </div>
          )}
        </div>
        <span className="shrink-0 tabular-nums font-semibold text-zinc-900 dark:text-white">
          {formatCurrency(org.amount)}
        </span>
      </div>
    </li>
  );
}

export function MonthDetailSections({ detail }: { detail: PnlMonthDetail }) {
  const { spend, revenue, net } = detail;
  const byCategory = [...spend.byCategory].sort((a, b) => b.amount - a.amount);
  const byVendor = [...spend.byVendor].sort((a, b) => b.amount - a.amount);
  const byOrg = [...revenue.byOrg].sort((a, b) => b.amount - a.amount);

  return (
    <div className="space-y-6">
      {/* Month totals */}
      <div className="grid gap-6 sm:grid-cols-3">
        <MiniMetric label="Spend" value={formatCurrency(spend.total)} />
        <MiniMetric label="Revenue" value={formatCurrency(revenue.total)} />
        <MiniMetric label="Net" value={signedCurrency(net)} valueClass={netClass(net)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Spend by category */}
        <SectionCard title="Spend by category">
          {byCategory.length === 0 ? (
            <EmptyRow>No spend this month.</EmptyRow>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {byCategory.map((c) => (
                  <tr
                    key={c.category}
                    className="border-b border-zinc-100 dark:border-zinc-700/60 last:border-0"
                  >
                    <td className="py-2.5">
                      <span className="flex items-center gap-2.5">
                        <span
                          className="h-3 w-3 shrink-0 rounded-sm"
                          style={{ backgroundColor: categoryColor(c.category) }}
                        />
                        <span className="text-zinc-700 dark:text-zinc-300">{c.category}</span>
                      </span>
                    </td>
                    <td className="py-2.5 text-right tabular-nums font-medium text-zinc-900 dark:text-white">
                      {formatCurrency(c.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        {/* Spend by vendor */}
        <SectionCard title="Spend by vendor">
          {byVendor.length === 0 ? (
            <EmptyRow>No spend this month.</EmptyRow>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {byVendor.map((v) => (
                    <tr
                      key={v.vendor}
                      className="border-b border-zinc-100 dark:border-zinc-700/60 last:border-0"
                    >
                      <td className="py-2.5 text-zinc-700 dark:text-zinc-300">{v.vendor}</td>
                      <td className="py-2.5 text-right tabular-nums font-medium text-zinc-900 dark:text-white">
                        {formatCurrency(v.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Revenue by org */}
      <SectionCard title="Revenue by org">
        {byOrg.length === 0 ? (
          <EmptyRow>No revenue this month.</EmptyRow>
        ) : (
          <ul className="space-y-3">
            {byOrg.map((org) => (
              <OrgRow key={org.orgId ?? org.billingEmail ?? org.orgName} org={org} />
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
